/**
 * Time and count formatting for the document page.
 *
 * Pulled out of the components because these are the parts with rules worth
 * pinning: a run that has not finished is measured against *now*, a run that
 * never started has no duration at all, and an unmeasured count renders as a
 * dash rather than a confident zero.
 *
 * @module regulation/document/documentTime
 */

export interface JobTimestamps {
  readonly createdAt: string | null;
  readonly startedAt: string | null;
  readonly completedAt: string | null;
}

const parse = (value: string | null): number | null => {
  if (value === null) return null;
  const time = new Date(value).getTime();
  return Number.isNaN(time) ? null : time;
};

/** "just now" → "5m ago" → "3d ago". Coarse: the exact minute is never asked. */
export function timeAgo(value: string | null, now: number): string {
  const then = parse(value);
  if (then === null) return "—";
  const seconds = Math.max(0, Math.floor((now - then) / 1000));
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

export function formatDuration(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  return `${minutes}m ${seconds % 60}s`;
}

/**
 * How long a run has taken. A finished run is measured to its completion; a
 * running one to *now*, so the number ticks while the work is happening. A
 * run with no start time has not begun, and reports zero rather than counting
 * from the moment the row was created.
 */
export function jobDurationSeconds(job: JobTimestamps | null, now: number): number {
  if (job === null) return 0;
  const start = parse(job.startedAt) ?? parse(job.createdAt);
  if (start === null) return 0;
  const end = parse(job.completedAt) ?? now;
  return Math.max(0, Math.floor((end - start) / 1000));
}

/** Sum of every run's duration — "total" beside "this job". */
export function totalDurationSeconds(
  jobs: ReadonlyArray<JobTimestamps>,
  now: number,
): number {
  return jobs.reduce((total, job) => total + jobDurationSeconds(job, now), 0);
}

/** A measured count, grouped; `null` (never measured) shows as an em dash. */
export function formatCount(value: number | null | undefined): string {
  return value === null || value === undefined ? "—" : value.toLocaleString();
}

/** Milliseconds as a duration, for the numbers ingestion records in ms. */
export function formatMs(value: number | null | undefined): string {
  return value === null || value === undefined
    ? "—"
    : formatDuration(Math.floor(value / 1000));
}

/** Absolute timestamp for hover titles — the precision the page omits. */
export function formatTimestamp(value: string | null): string {
  const time = parse(value);
  if (time === null) return "—";
  return new Date(time).toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });
}
